import React, { useEffect, useState } from 'react';
import { Table, Form, Row, Col, Card, Spinner, Button, Pagination, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchChatLogs, fetchFeedbackStats } from '../features/logSlice';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../lib/api';
import AdminLayout from '../components/AdminLayout';

const ChatLogsPage = () => {
  const dispatch = useDispatch();
  const { list, loading, feedbackCounts, totalPages } = useSelector((state) => state.logs);
  const token = useSelector((state) => state.auth.token);

  const [filters, setFilters] = useState({
    email: '',
    ip_address: '',
    guest_name: '',
    feedback: '',
  });

  const [page, setPage] = useState(1);
  const [actMsg, setActMsg] = useState('');
  const [actErr, setActErr] = useState('');
  const [identityDel, setIdentityDel] = useState('');
  const [identityKeys, setIdentityKeys] = useState('');

  useEffect(() => {
    dispatch(fetchChatLogs({ token, filters, page }));
    dispatch(fetchFeedbackStats(token));
  }, [dispatch, token, filters, page]);

  const handleSearch = () => {
    setPage(1);
    dispatch(fetchChatLogs({ token, filters, page: 1 }));
  };

  const count = (type) =>
    feedbackCounts.find((item) => item.feedback === type)?.total || 0;

  const exportCSV = () => {
    const rows = list.map((log) => ({
      user: log.email || log.guest_name || 'Anonymous',
      feedback: log.feedback || '-',
      question: log.question,
      response: log.response,
      ip: log.ip_address,
      time: new Date(log.created_at).toLocaleString(),
      thread: log.thread_id || '-',
    }));

    const csv = [
      ['User', 'Feedback', 'Question', 'Response', 'IP', 'Time', 'Thread'],
      ...rows.map((r) => Object.values(r)),
    ]
      .map((row) => row.map((val) => `"${String(val).replaceAll('"', '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'chat_logs.csv');
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [['User', 'Feedback', 'Question', 'Response', 'IP', 'Time', 'Thread']],
      body: list.map((log) => [
        log.email || log.guest_name || 'Anonymous',
        log.feedback || '-',
        log.question,
        log.response,
        log.ip_address,
        new Date(log.created_at).toLocaleString(),
        log.thread_id || '-',
      ]),
      styles: { cellWidth: 'wrap' },
      columnStyles: {
        2: { cellWidth: 60 },
        3: { cellWidth: 60 },
        6: { cellWidth: 50 },
      },
      theme: 'striped',
    });
    doc.save('chat_logs.pdf');
  };

  const doDeleteThreadChats = async (thread_id) => {
    try {
      if (!thread_id) return;
      await api.delete(`/admin/chats/${encodeURIComponent(thread_id)}/`);
      setActMsg(`Deleted chats for thread: ${thread_id}`);
      dispatch(fetchChatLogs({ token, filters, page }));
      setTimeout(() => setActMsg(''), 2000);
    } catch (err) {
      setActErr(err?.response?.data?.error || err?.message || 'Delete failed');
      setTimeout(() => setActErr(''), 3000);
    }
  };

  const doDeleteThreadSummary = async (thread_id) => {
    try {
      if (!thread_id) return;
      await api.delete(`/admin/summary/${encodeURIComponent(thread_id)}/`);
      setActMsg(`Deleted summary for thread: ${thread_id}`);
      setTimeout(() => setActMsg(''), 2000);
    } catch (err) {
      setActErr(err?.response?.data?.error || err?.message || 'Delete failed');
      setTimeout(() => setActErr(''), 3000);
    }
  };

  const doDeleteIdentityMemory = async () => {
    try {
      if (!identityDel.trim()) {
        setActErr('Identity is required');
        setTimeout(() => setActErr(''), 2000);
        return;
      }

      const params = new URLSearchParams({ identity: identityDel.trim() });
      identityKeys
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((k) => params.append('key', k));

      await api.delete(`/admin/memory/?${params.toString()}`);
      setActMsg(
        `Deleted memory for identity: ${identityDel}${
          identityKeys.trim() ? ` (keys: ${identityKeys})` : ''
        }`
      );
      setIdentityDel('');
      setIdentityKeys('');
      setTimeout(() => setActMsg(''), 2500);
    } catch (err) {
      setActErr(err?.response?.data?.error || err?.message || 'Delete failed');
      setTimeout(() => setActErr(''), 3000);
    }
  };

  return (
    <AdminLayout
      title="Chat Logs"
      subtitle="Search conversations, export data, and perform thread-level maintenance actions."
    >
      {actMsg ? <Alert variant="success">{actMsg}</Alert> : null}
      {actErr ? <Alert variant="danger">{actErr}</Alert> : null}

      <Card className="admin-filter-card mb-4">
        <h6 className="fw-bold mb-3">Admin Tools</h6>
        <Row className="g-3 align-items-end">
          <Col lg={5}>
            <Form.Label>Delete Identity Memory</Form.Label>
            <Form.Control
              placeholder="identity e.g. email:ada@x.com"
              value={identityDel}
              onChange={(e) => setIdentityDel(e.target.value)}
            />
          </Col>
          <Col lg={5}>
            <Form.Label>Limit to keys</Form.Label>
            <Form.Control
              placeholder="style.tone, location"
              value={identityKeys}
              onChange={(e) => setIdentityKeys(e.target.value)}
            />
          </Col>
          <Col lg={2}>
            <Button
              variant="danger"
              className="w-100 admin-action-btn"
              onClick={doDeleteIdentityMemory}
            >
              Delete Memory
            </Button>
          </Col>
        </Row>
      </Card>

      <Row className="g-3 mb-4">
        <Col md={6}>
          <div className="admin-stat-card">
            <div className="text-muted small fw-semibold mb-2">Positive Feedback</div>
            <h3 className="mb-0 fw-bold text-success">{count('positive')}</h3>
          </div>
        </Col>
        <Col md={6}>
          <div className="admin-stat-card">
            <div className="text-muted small fw-semibold mb-2">Negative Feedback</div>
            <h3 className="mb-0 fw-bold text-danger">{count('negative')}</h3>
          </div>
        </Col>
      </Row>

      <Card className="admin-filter-card mb-4">
        <Row className="g-3">
          <Col md={3}>
            <Form.Control
              placeholder="Email"
              value={filters.email}
              onChange={(e) => setFilters({ ...filters, email: e.target.value })}
            />
          </Col>
          <Col md={3}>
            <Form.Control
              placeholder="IP Address"
              value={filters.ip_address}
              onChange={(e) => setFilters({ ...filters, ip_address: e.target.value })}
            />
          </Col>
          <Col md={3}>
            <Form.Control
              placeholder="Guest Name"
              value={filters.guest_name}
              onChange={(e) => setFilters({ ...filters, guest_name: e.target.value })}
            />
          </Col>
          <Col md={3}>
            <Form.Select
              value={filters.feedback}
              onChange={(e) => setFilters({ ...filters, feedback: e.target.value })}
            >
              <option value="">All Feedback</option>
              <option value="positive">Positive</option>
              <option value="negative">Negative</option>
            </Form.Select>
          </Col>
        </Row>

        <div className="d-flex justify-content-end gap-2 mt-3 flex-wrap">
          <Button className="admin-action-btn" onClick={handleSearch}>
            Search Logs
          </Button>
          <Button variant="outline-primary" className="admin-action-btn" onClick={exportCSV}>
            Export CSV
          </Button>
          <Button variant="outline-dark" className="admin-action-btn" onClick={exportPDF}>
            Export PDF
          </Button>
        </div>
      </Card>

      <Card className="admin-surface-card">
        <Card.Body>
          <div className="admin-section-title">Conversation Logs</div>

          <div className="table-responsive">
            <Table hover className="admin-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Email / Guest</th>
                  <th>Feedback</th>
                  <th>Question</th>
                  <th>Response</th>
                  <th>IP</th>
                  <th>Agent</th>
                  <th>Time</th>
                  <th>Thread</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-5">
                      <Spinner animation="border" />
                    </td>
                  </tr>
                ) : list.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-4">
                      No logs found
                    </td>
                  </tr>
                ) : (
                  list.map((log) => (
                    <tr key={log.id}>
                      <td>{log.email || log.guest_name || 'Anonymous'}</td>
                      <td>{log.feedback || '-'}</td>
                      <td>{log.question}</td>
                      <td>{log.response}</td>
                      <td>{log.ip_address}</td>
                      <td>{log.user_agent?.slice(0, 24)}...</td>
                      <td>{new Date(log.created_at).toLocaleString()}</td>
                      <td style={{ maxWidth: 220, wordBreak: 'break-word' }}>
                        {log.thread_id || '-'}
                      </td>
                      <td>
                        <div className="d-flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline-danger"
                            className="admin-action-btn"
                            onClick={() => doDeleteThreadChats(log.thread_id)}
                            disabled={!log.thread_id}
                          >
                            Del Chats
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-warning"
                            className="admin-action-btn"
                            onClick={() => doDeleteThreadSummary(log.thread_id)}
                            disabled={!log.thread_id}
                          >
                            Del Summary
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 ? (
            <Pagination className="justify-content-center mt-4">
              {[...Array(totalPages)].map((_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={page === i + 1}
                  onClick={() => setPage(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
            </Pagination>
          ) : null}
        </Card.Body>
      </Card>
    </AdminLayout>
  );
};

export default ChatLogsPage;